import {
  Component,
  Input,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  ViewChild,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import {
  RemoteParticipant,
  RemoteAudioTrack,
  RemoteVideoTrack,
  RemoteTrack,
  RemoteDataTrack
} from 'twilio-video';
import {NgClass, NgIf} from "@angular/common";
import {IconComponent} from "@shared/components/icon/icon.component";


@Component({
  selector: 'app-participant-view',
  template: `
    <div #container
         [class.large]="size==='large'"
         [class.small]="size==='small'"
         class="overflow-hidden rounded shadow-lg relative">
      <!-- Video disabled: full overlay -->
      @if (!videoEnabled) {
        <div
             class="absolute inset-0 bg-black/60 flex items-center justify-center text-white z-20 pointer-events-none">
          <app-icon icon="mdi-video-off" [fontSize]="size==='large' ? '64px' : '42px'"></app-icon>
        </div>
      }

      @if (!audioEnabled) {
        <div
          class="absolute z-20 pointer-events-none"
          [ngClass]="size==='large' ? 'left-1/2 -translate-x-1/2 top-4' : 'top-2 right-2'">
          <div
            [ngClass]="size==='large'
            ? 'px-3 py-1.5 rounded-full bg-black/80 text-white/95 flex items-center gap-2 shadow-md ring-1 ring-white/15'
            : 'p-1 rounded-full bg-black/70 text-white shadow-sm'">
            <app-icon icon="mdi-microphone-off"
                      [lineHeight]="size==='large' ? '22px' : '18px'"
                      [fontSize]="size==='large' ? '22px' : '18px'"></app-icon>
            @if (size === 'large') {
              <span class="text-sm font-medium select-none subpixel-antialiased">Muted</span>
            }
          </div>
        </div>
      }

    </div>
  `,
  styleUrls: ['./participant-view.component.scss'],
  imports: [
    IconComponent,
    NgClass,
  ],
  standalone: true
})
class ParticipantViewComponent implements AfterViewInit, OnDestroy, OnChanges {
  @Input() participant!: RemoteParticipant;
  @Input() size: 'large' | 'small' = 'small';
  @ViewChild('container', { static: true }) container!: ElementRef<HTMLDivElement>;

  videoEnabled = true;
  audioEnabled = true;

  private _onTrackSub = (track: RemoteTrack) => {
    switch (track.kind) {
      case 'video':
        this.container.nativeElement.appendChild((track as RemoteVideoTrack).attach());
        break;
      case 'audio':
        this.container.nativeElement.appendChild((track as RemoteAudioTrack).attach());
        break;
      case 'data':
        const dt = track as RemoteDataTrack;
        dt.removeAllListeners?.('message');
        dt.on('message', msg => this._onDataMessage(msg));
        break;
    }
  };

  private _onDataMessage(msg: string | ArrayBuffer) {
    const text = typeof msg === 'string' ? msg : new TextDecoder().decode(new Uint8Array(msg as ArrayBuffer));
    try {
      const payload = JSON.parse(text);
      switch (payload.type) {
        case 'videoEnabled':
          this.videoEnabled = !!payload.enabled;
          break;
        case 'audioEnabled':
          this.audioEnabled = !!payload.enabled;
          break;
        case 'code':
          switch (payload.code) {
            case 'local_video_enabled':
              this.videoEnabled = true;
              break;
            case 'local_video_disabled':
              this.videoEnabled = false;
              break;
            case 'local_audio_enabled':
              this.audioEnabled = true;
              break;
            case 'local_audio_disabled':
              this.audioEnabled = false;
              break;
          }
          break;
      }
    } catch (err) {
      console.warn('[Twilio][data] parse error:', text);
    }
  }

  private _onTrackUnsub = (track: RemoteTrack) => {
    (track as RemoteAudioTrack | RemoteVideoTrack)
      .detach()
      .forEach(el => el.remove());
    if ((track as any).kind === 'data') {
      (track as RemoteDataTrack).removeAllListeners?.('message');
    }
  };

  ngAfterViewInit() {
    this._setupParticipant(this.participant);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['participant'] && !changes['participant'].isFirstChange()) {
      this._cleanupParticipant(changes['participant'].previousValue);
      this._setupParticipant(this.participant);
    }
  }

  ngOnDestroy() {
    this._cleanupParticipant(this.participant);
  }

  private _setupParticipant(p: RemoteParticipant) {
    p.tracks.forEach(pub => {
      const t = pub.track;
      if (!t) return;
      if (t.kind === 'video' || t.kind === 'audio') {
        this.container.nativeElement.appendChild((t as any).attach());
      } else if (t.kind === 'data') {
        const dt = t as RemoteDataTrack;
        dt.removeAllListeners?.('message');
        dt.on('message', msg => this._onDataMessage(msg));
      }
    });
    p.on('trackSubscribed', this._onTrackSub);
    p.on('trackUnsubscribed', this._onTrackUnsub);
  }

  private _cleanupParticipant(p: RemoteParticipant) {
    p.tracks.forEach(pub => {
      const t = pub.track;
      if (t?.kind === 'data') (t as RemoteDataTrack).removeAllListeners?.('message');
    });
    p.off('trackSubscribed', this._onTrackSub);
    p.off('trackUnsubscribed', this._onTrackUnsub);
    this.container.nativeElement.querySelectorAll('video, audio').forEach(el => el.remove());
  }
}

export default ParticipantViewComponent
